import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Descriptions, Input, Space, Switch, Typography, message } from 'antd';
import { ReloadOutlined, PlayCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { responsibilityIntelligenceService } from '@/services/responsibilityIntelligence.service';

const { Title, Paragraph, Text } = Typography;

/**
 * Minimal admin for RIE-PO-DELIVERY-001 (company-scoped enable + shadow + dry-run).
 */
export const ResponsibilityRulesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [growthEnabled, setGrowthEnabled] = useState(false);
  const [shadowMode, setShadowMode] = useState(true);
  const [rule, setRule] = useState<any>(null);
  const [dryRun, setDryRun] = useState<any>(null);
  const [diagPo, setDiagPo] = useState('NTES-PO-2026-0200');
  const [diag, setDiag] = useState<any>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [status, active] = await Promise.all([
        responsibilityIntelligenceService.companyStatus(),
        responsibilityIntelligenceService.getPoDeliveryRule().catch(() => null),
      ]);
      setEnabled(!!status.enabled);
      setGrowthEnabled(!!status.growthEnabled);
      setShadowMode(status.shadowMode !== false);
      setRule(active);
    } catch (e: any) {
      message.error(e?.message || 'Failed to load Responsibility Rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const saveStatus = async (next: {
    enabled?: boolean;
    growthEnabled?: boolean;
    shadowMode?: boolean;
  }) => {
    setSaving(true);
    try {
      const data = await responsibilityIntelligenceService.setCompanyStatus(next);
      setEnabled(!!data.enabled);
      setGrowthEnabled(!!data.growthEnabled);
      setShadowMode(!!data.shadowMode);
      message.success('Saved');
    } catch (e: any) {
      message.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runDryRun = async () => {
    setSaving(true);
    try {
      const data = await responsibilityIntelligenceService.dryRun();
      setDryRun(data);
      message.success('Dry-run complete (no obligations written)');
    } catch (e: any) {
      message.error(e?.message || 'Dry-run failed');
    } finally {
      setSaving(false);
    }
  };

  const runRecon = async () => {
    setSaving(true);
    try {
      const data = await responsibilityIntelligenceService.runRecon();
      message.success(`Recon finished — processed ${data?.processed ?? 0}`);
      await refresh();
    } catch (e: any) {
      message.error(e?.message || 'Recon failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleSuspend = async (suspended: boolean) => {
    setSaving(true);
    try {
      await responsibilityIntelligenceService.suspendRule(suspended);
      message.success(suspended ? 'Rule suspended' : 'Rule resumed');
      await refresh();
    } catch (e: any) {
      message.error(e?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const runDiagnostics = async () => {
    if (!diagPo.trim()) {
      message.warning('Enter a PO number');
      return;
    }
    setSaving(true);
    try {
      const data = await responsibilityIntelligenceService.diagnosePoDelivery(diagPo.trim());
      setDiag(data);
      message.success('Diagnostics ready');
    } catch (e: any) {
      message.error(e?.message || 'Diagnostics failed');
    } finally {
      setSaving(false);
    }
  };

  const cfg = rule?.config || {};

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <Title level={3} style={{ marginTop: 0 }}>
        Responsibility Rules
      </Title>
      <Paragraph type="secondary">
        Platform Responsibility Intelligence Engine — Supply Chain{' '}
        <Text code>RIE-PO-DELIVERY-001</Text> and Growth &amp; Revenue packs (
        <Text code>RIE-GROWTH-*</Text>). Projects monitoring obligations into My Workboard. Does not
        change source document lifecycles.
      </Paragraph>

      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="Company-scoped activation"
        description="Enable for the active company, keep Shadow mode on until dry-run results look correct, then run recon and turn shadow off to allow threshold notifications."
      />

      <Card loading={loading} title="Company activation" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space>
            <Switch
              checked={enabled}
              disabled={saving}
              onChange={(v) => void saveStatus({ enabled: v })}
            />
            <Text>Enable PO delivery monitoring (Supply Chain)</Text>
          </Space>
          <Space>
            <Switch
              checked={growthEnabled}
              disabled={saving}
              onChange={(v) => void saveStatus({ growthEnabled: v })}
            />
            <Text>Enable Growth &amp; Revenue responsibility packs</Text>
          </Space>
          <Space>
            <Switch
              checked={shadowMode}
              disabled={saving || (!enabled && !growthEnabled)}
              onChange={(v) => void saveStatus({ shadowMode: v })}
            />
            <Text>Shadow mode (evaluate obligations, suppress notifications)</Text>
          </Space>
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => void refresh()} disabled={saving}>
              Refresh
            </Button>
            <Button onClick={() => void runDryRun()} loading={saving}>
              Dry-run preview
            </Button>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => void runRecon()}
              disabled={!enabled && !growthEnabled}
              loading={saving}
            >
              Run reconciliation
            </Button>
          </Space>
        </Space>
      </Card>

      <Card title="Active rule" style={{ marginBottom: 16 }} loading={loading}>
        {rule ? (
          <>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Code">RIE-PO-DELIVERY-001</Descriptions.Item>
              <Descriptions.Item label="Version">{rule.version}</Descriptions.Item>
              <Descriptions.Item label="Advance days">{cfg.advanceDays ?? 14}</Descriptions.Item>
              <Descriptions.Item label="Suspended">
                <Switch
                  checked={!!rule.is_suspended}
                  disabled={saving}
                  onChange={(v) => void toggleSuspend(v)}
                />
              </Descriptions.Item>
            </Descriptions>
          </>
        ) : (
          <Text type="secondary">No active rule found — run migration 554.</Text>
        )}
      </Card>

      {dryRun ? (
        <Card title="Last dry-run" style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Matching">{dryRun.matching}</Descriptions.Item>
            <Descriptions.Item label="Approaching">{dryRun.approaching}</Descriptions.Item>
            <Descriptions.Item label="Due today">{dryRun.dueToday}</Descriptions.Item>
            <Descriptions.Item label="Overdue">{dryRun.overdue}</Descriptions.Item>
            <Descriptions.Item label="Ownership exceptions">
              {dryRun.ownershipExceptions}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ) : null}

      <Card title="Responsibility Resolution Diagnostics">
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          Explain why a PO delivery responsibility is visible — or not — for the logged-in admin
          (or pass userId via API). Does not mutate obligations.
        </Paragraph>
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            style={{ width: 280 }}
            value={diagPo}
            onChange={(e) => setDiagPo(e.target.value)}
            placeholder="PO number or id"
            onPressEnter={() => void runDiagnostics()}
          />
          <Button icon={<SearchOutlined />} loading={saving} onClick={() => void runDiagnostics()}>
            Diagnose
          </Button>
        </Space>
        {diag ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Purchase Order">{diag.poNumber}</Descriptions.Item>
            <Descriptions.Item label="Rule">
              <Text code>{diag.ruleCode}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Expected delivery">
              {diag.expectedDelivery || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Lead time">{diag.leadTimeDays} days</Descriptions.Item>
            <Descriptions.Item label="Responsible role">{diag.responsibleRole}</Descriptions.Item>
            <Descriptions.Item label="Source eligibility">
              {diag.sourceEligibility?.eligible ? 'Eligible' : 'Not eligible'} —{' '}
              {diag.sourceEligibility?.detail}
            </Descriptions.Item>
            <Descriptions.Item label="Resolved owner">
              {diag.resolvedOwnership?.individualOwnerEmail ||
                diag.resolvedOwnership?.individualOwnerId ||
                '—'}{' '}
              ({diag.resolvedOwnership?.method || 'none'}
              {diag.resolvedOwnership?.ownershipException ? ', ownership exception' : ''})
            </Descriptions.Item>
            <Descriptions.Item label="Shared monitors">
              {diag.resolvedOwnership?.sharedUserCount ?? 0}
              {diag.resolvedOwnership?.sharedUserEmails?.length
                ? ` — ${diag.resolvedOwnership.sharedUserEmails.join(', ')}`
                : ''}
            </Descriptions.Item>
            <Descriptions.Item label="User authorization">
              {diag.userAuthorization?.visible ? 'Visible' : 'Not visible'} —{' '}
              {diag.userAuthorization?.reason}
            </Descriptions.Item>
            <Descriptions.Item label="Projection state">
              {diag.projectionState?.conditionCode || '—'} /{' '}
              {diag.projectionState?.bucketIfVisible || 'no bucket'} —{' '}
              {diag.projectionState?.detail}
            </Descriptions.Item>
            <Descriptions.Item label="Business date">
              {diag.businessDate} ({diag.businessTimezone})
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Card>
    </div>
  );
};

export default ResponsibilityRulesPage;
